<template>
  <section
    class="dashboard-section recent-contents"
    aria-labelledby="recent-contents-heading"
  >
    <header class="dashboard-section__header">
      <h2 id="recent-contents-heading" class="dashboard-section__title">
        <i class="uil-presentation-check" aria-hidden="true"></i>
        {{ $t("general.recently_added") }}
      </h2>
      <div class="dashboard-section__right section-header-right">
        <router-link
          :to="{ name: 'company.contents.all' }"
          class="dashboard-section__action"
        >
          <i class="uil-list-ui-alt" aria-hidden="true"></i>
          {{ $t("home.see_all") }}
        </router-link>
      </div>
    </header>

    <div v-if="items.length" class="recent-contents__grid">
      <article v-for="item in items" :key="item.id" class="recent-content-card">
        <img
          :alt="item.name || item.title || ''"
          :src="item.cover_photo_link"
          class="recent-content-card__image"
          decoding="async"
          loading="lazy"
        />
        <div class="recent-content-card__body">
          <p class="recent-content-card__category">{{ item.title }}</p>
          <h3 class="recent-content-card__title">{{ item.name }}</h3>
          <div class="recent-content-card__actions">
            <router-link
              v-if="canEdit"
              :aria-label="$t('faq_categories.edit') + ': ' + item.name"
              :to="{
                name: 'company.contents.edit',
                params: { id: item.id },
              }"
              class="recent-content-card__action recent-content-card__action--edit"
            >
              <i class="uil-cog" aria-hidden="true"></i>
              {{ $t("faq_categories.edit") }}
            </router-link>
            <button
              v-else
              class="recent-content-card__action"
              disabled
              type="button"
            >
              <i class="uil-cog" aria-hidden="true"></i>
              {{ $t("faq_categories.edit") }}
            </button>
            <router-link
              v-if="canCreateAssignment"
              :aria-label="
                $t('menu.company_dashboard.create_assignment') +
                ': ' +
                item.name
              "
              :to="{
                name: 'company.assignments.add',
                params: {
                  id: item.id,
                  name: item.name,
                  title: item.title,
                },
              }"
              class="recent-content-card__action recent-content-card__action--assignment"
            >
              <i class="uil-location-arrow" aria-hidden="true"></i>
              {{ $t("menu.company_dashboard.create_assignment") }}
            </router-link>
            <button
              v-else
              class="recent-content-card__action"
              disabled
              type="button"
            >
              <i class="uil-location-arrow" aria-hidden="true"></i>
              {{ $t("menu.company_dashboard.create_assignment") }}
            </button>
          </div>
        </div>
        <footer v-if="item.createdAtLabel" class="recent-content-card__footer">
          <time :datetime="item.created_at">
            <i class="uil-calendar-alt" aria-hidden="true"></i>
            {{ item.createdAtLabel }}
          </time>
        </footer>
      </article>
    </div>
  </section>
</template>

<script>
export default {
  name: "DashboardRecentContents",
  props: {
    canCreateAssignment: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
};
</script>

<style scoped>
.dashboard-section {
  margin-bottom: 24px;
}

.dashboard-section__header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.dashboard-section__title {
  align-items: center;
  color: #1f2937;
  display: flex;
  font-size: 20px;
  font-weight: 600;
  gap: 8px;
  line-height: 1.4;
  margin: 0;
  min-width: 0;
}

.dashboard-section__action {
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  color: #374151;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 500;
  gap: 4px;
  justify-content: center;
  min-height: 48px;
  padding: 8px 12px;
  text-decoration: none;
  touch-action: manipulation;
}

.dashboard-section__right {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  min-width: 0;
}

.dashboard-section__action:active {
  opacity: 0.85;
  transform: scale(0.98);
}

.dashboard-section__action:focus-visible,
.recent-content-card__action:focus-visible {
  outline: 3px solid #2563eb;
  outline-offset: 2px;
}

.recent-contents__grid {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
}

.recent-content-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.recent-content-card__image {
  background: #f3f4f6;
  height: 176px;
  object-fit: cover;
  width: 100%;
}

.recent-content-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

.recent-content-card__category {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
}

.recent-content-card__title {
  color: #1f2937;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
  overflow-wrap: anywhere;
}

.recent-content-card__actions {
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr);
  margin-top: auto;
  padding-top: 8px;
}

.recent-content-card__action {
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  color: #374151;
  display: inline-flex;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  justify-content: center;
  line-height: 1.4;
  min-height: 42px;
  padding: 8px 12px;
  text-align: center;
  text-decoration: none;
  touch-action: manipulation;
}

.recent-content-card__action--edit {
  background: var(--successColor);
  border-color: var(--successColor);
  color: #fff;
}

.recent-content-card__action--assignment {
  background: var(--infoColor);
  border-color: var(--infoColor);
  color: #fff;
}

.recent-content-card__action:active:not(:disabled) {
  opacity: 0.85;
  transform: scale(0.98);
}

.recent-content-card__action:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.recent-content-card__footer {
  border-top: 1px solid #e5e7eb;
  color: #4b5563;
  font-size: 12px;
  line-height: 1.4;
  padding: 12px 16px;
}

.recent-content-card__footer time {
  align-items: center;
  display: flex;
  gap: 4px;
}

@supports (content-visibility: auto) {
  .recent-content-card {
    contain-intrinsic-size: 390px;
    content-visibility: auto;
  }
}

@media (min-width: 640px) {
  .recent-contents__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recent-content-card__actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 960px) {
  .recent-contents__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .dashboard-section__header {
    align-items: stretch;
    flex-direction: column;
  }

  .dashboard-section__right.section-header-right {
    align-items: stretch;
    display: flex !important;
    flex: 0 0 auto;
    opacity: 1 !important;
    visibility: visible !important;
    width: 100%;
  }

  .dashboard-section__action {
    width: 100%;
  }

  .recent-contents__grid {
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);
  }

  .recent-content-card__actions {
    grid-template-columns: minmax(0, 1fr);
  }

  .recent-content-card {
    border-color: #edf0f3;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .recent-content-card__image {
    height: 128px;
  }

  .recent-content-card__body {
    padding: 12px;
  }

  .recent-content-card__footer {
    padding: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-section__action:active,
  .recent-content-card__action:active:not(:disabled) {
    transform: none;
  }
}
</style>
